import {
  AmbientLight,
  DirectionalLight,
  Mesh,
  MeshLambertMaterial,
  PlaneGeometry,
  PerspectiveCamera,
  Scene,
  ShadowMaterial,
  WebGLRenderer,
} from 'three';
import type { CitizenAsset, CitizenLod, CitizenClip } from '../world/life/citizens/citizenAsset';
import { clipPoseAt, skinnedVertex } from '../world/life/citizens/citizenPose';
import { visitorFigure, visitorGeometry, type Clothing } from './visitorAppearance';
import { CitizenGreeting } from './citizenGreeting';

export function createCitizenPreview(
  canvas: HTMLCanvasElement,
  asset: CitizenAsset,
  character: 'male' | 'female'
) {
  const figure = visitorFigure(asset, character);
  const lod = figure.lods[0];
  const idle = asset.clips[figure.clips.idle];
  const greet = asset.clips[character === 'female' ? 'Curtsy' : 'Wave'];
  if (!lod || !idle || !greet) throw new Error('Citizen preview is incomplete');
  const renderer = previewRenderer(canvas);
  const scene = new Scene();
  const material = new MeshLambertMaterial({ vertexColors: true, flatShading: true });
  const mesh = new Mesh(visitorGeometry(asset, lod, character, 'russet'), material);
  const light = lightPreview(scene, mesh);
  const floor = previewFloor(scene);
  const greeting = new CitizenGreeting(greet.seconds);
  const stop = previewLoop(canvas, renderer, scene, previewCamera(), (now) => {
    const phase = greeting.phase(now);
    posePreview(mesh, asset, lod, idle, greet, now / 1000, phase ?? 0, phase === null ? 0 : 1);
  });
  return {
    selected(value: boolean): void {
      light.intensity = value ? 3.2 : 2.3;
      greeting.select(value, performance.now());
    },
    colour(choice: Clothing): void {
      mesh.geometry.dispose();
      mesh.geometry = visitorGeometry(asset, lod, character, choice);
    },
    dispose(): void {
      stop();
      mesh.geometry.dispose();
      material.dispose();
      floor.dispose();
      light.shadow.map?.dispose();
      renderer.dispose();
      renderer.forceContextLoss();
    },
  };
}

function previewRenderer(canvas: HTMLCanvasElement): WebGLRenderer {
  const renderer = new WebGLRenderer({ canvas, alpha: true, antialias: true });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.shadowMap.enabled = true;
  return renderer;
}

function previewCamera(): PerspectiveCamera {
  const camera = new PerspectiveCamera(32, 1, 0.1, 20);
  camera.position.set(3.7, 1.4, 0.65);
  camera.lookAt(0, 0.95, 0);
  return camera;
}

function previewLoop(
  canvas: HTMLCanvasElement,
  renderer: WebGLRenderer,
  scene: Scene,
  camera: PerspectiveCamera,
  pose: (now: number) => void
): () => void {
  let frame = 0;
  const render = (now: number): void => {
    const width = canvas.clientWidth,
      height = canvas.clientHeight;
    if (width && height) {
      if (
        canvas.width !== Math.round(width * renderer.getPixelRatio()) ||
        canvas.height !== Math.round(height * renderer.getPixelRatio())
      ) {
        renderer.setSize(width, height, false);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
      }
      pose(now);
      renderer.render(scene, camera);
    }
    frame = requestAnimationFrame(render);
  };
  frame = requestAnimationFrame(render);
  return () => {
    cancelAnimationFrame(frame);
  };
}

function posePreview(
  mesh: Mesh,
  asset: CitizenAsset,
  lod: CitizenLod,
  idle: CitizenClip,
  greet: CitizenClip,
  seconds: number,
  phase: number,
  greeting: number
): void {
  const positions = mesh.geometry.getAttribute('position');
  const pose = clipPoseAt(idle, seconds);
  const greetingPose = clipPoseAt(greet, phase);
  const blend = greeting * Math.max(0, Math.min(1, phase * 5, (greet.seconds - phase) * 5));
  for (let t = 0; t < lod.triangles; t++)
    for (let c = 0; c < 3; c++) {
      const index = lod.faces[t * 6 + c];
      if (index === undefined) throw new Error('Citizen preview has an invalid face');
      positions.setXYZ(t * 3 + c, ...previewVertex(asset, lod, index, pose, greetingPose, blend));
    }
  positions.needsUpdate = true;
  mesh.geometry.computeVertexNormals();
}

/** The long skirt gathers against the floor as the knees bend instead of sinking through it. */
export function previewVertex(
  asset: CitizenAsset,
  lod: CitizenLod,
  vertex: number,
  from: number,
  to: number,
  mix: number
): [number, number, number] {
  const point = skinnedVertex(asset, lod, vertex, from, to, mix);
  point[1] = Math.max(0, point[1]);
  return point;
}

function lightPreview(scene: Scene, mesh: Mesh): DirectionalLight {
  scene.add(mesh);
  mesh.castShadow = true;
  scene.add(new AmbientLight(0xe8d6ba, 1.6));
  const light = new DirectionalLight(0xffce88, 3.2);
  light.position.set(2, 7, 2);
  light.castShadow = true;
  light.shadow.mapSize.set(512, 512);
  light.shadow.camera.left = light.shadow.camera.bottom = -2;
  light.shadow.camera.right = light.shadow.camera.top = 2;
  light.shadow.bias = -0.002;
  scene.add(light);
  return light;
}

function previewFloor(scene: Scene) {
  const shadowGeometry = new PlaneGeometry(2.4, 2.4);
  const shadowMaterial = new ShadowMaterial({ opacity: 0.45 });
  const shadow = new Mesh(shadowGeometry, shadowMaterial);
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = -0.002;
  shadow.receiveShadow = true;
  scene.add(shadow);
  return {
    dispose(): void {
      shadowGeometry.dispose();
      shadowMaterial.dispose();
    },
  };
}
